// useFetch4.ts
// Добавление механизма игнорирования устаревших ответов при быстром обновлении зависимостей
import { useState, useEffect, useCallback, useRef } from "react";

export function useFetch<T>(
  apiCall: (payload?: any) => Promise<T>,
  defaultValue: T,
  initialPayload: any = {},
  dependencies: any[] = [],
  onLoad?: (data: T) => void, 
) {
  const [data, setData] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const [payload, setPayload] = useState(initialPayload);
  
  const onLoadRef = useRef(onLoad);
  useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);

  // Храним счетчик или уникальный токен текущего активного запроса
  const requestIdRef = useRef(0);

  const fetchData = useCallback(
    async (pl: any = payload) => {
      // Инкрементируем ID запроса при каждом старте. Это наш аналог isCurrent
      const currentRequestId = ++requestIdRef.current;
      
      setLoading(true);
      setError(null);
      try {
        const result = await apiCall(pl);
        
        // ЕСЛИ за время ожидания ответа прилетел новый запрос, 
        // то currentRequestId уже НЕ БУДЕТ равен requestIdRef.current.
        // И мы просто игнорируем этот устаревший результат.
        if (currentRequestId === requestIdRef.current) {
          setData(result);
          onLoadRef.current?.(result);
        }
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [apiCall, payload] // Теперь payload можно вернуть, так как requestIdRef решает проблемы гонок
  );

  // Слідкуємо за зміною залежностей
  useEffect(() => {
    setPayload(initialPayload);
    fetchData(initialPayload);

    // При размонтировании компонента инкрементируем счетчик, 
    // чтобы любые летящие в этот момент запросы были проигнорированы
    return () => {
      requestIdRef.current++;
    };
  }, dependencies); 

  const update = useCallback(
    async (newPayload: any = {}) => {
      setPayload(newPayload);
      await fetchData(newPayload);
    },
    [fetchData]
  );

  return { data, loading, error, setData, update, refresh: () => fetchData(payload) };
}